'use client'

import { useEffect, useRef } from 'react'
import StoreBadges from '@/app/components/StoreBadges'
import { track } from '@/utils/analytics'
import IPhoneFrame from './IPhoneFrame'

export interface ScanDemoVideoProps {
  variant?: 'hero' | 'inline'
  surface?: string
  eyebrow?: string
  headline?: string
  sub?: string
}

const MOMENTS = [
  { label: 'Identify the product and its context', detail: 'The recording uses a bottled-water example. Check the item and conditions returned by an identification.' },
  { label: 'Review the modeled assessment', detail: 'The interface shows an assigned score and product-contact information. These are not laboratory results or personal exposure measurements.' },
  { label: 'Compare a suggested alternative', detail: 'Review the alternative against your preference. A difference in model scores does not establish a health benefit.' },
]

// The asset is an existing iOS recording. It autoplays muted and loops, with
// no controls (owner decision, Oct 2026). It only pauses while scrolled off
// screen, to save battery, and resumes when it comes back into view.
export default function ScanDemoVideo({ variant = 'hero', surface, eyebrow, headline, sub }: ScanDemoVideoProps) {
  const videoRef = useRef<HTMLVideoElement | null>(null)
  const trackedImpression = useRef(false)
  const trackedPlay = useRef(false)
  const resolvedSurface = surface ?? (variant === 'hero' ? 'home_scan_demo' : 'inline_scan_demo')
  const pageType = variant === 'hero' ? 'home' : 'blog'

  useEffect(() => {
    const el = videoRef.current
    if (!el || typeof IntersectionObserver === 'undefined') return
    // Observe the video itself: a tall section cannot reach 40% on many phones.
    const observer = new IntersectionObserver((entries) => {
      const visible = entries.some(entry => entry.isIntersecting && entry.intersectionRatio >= 0.4)
      if (visible && !trackedImpression.current) {
        trackedImpression.current = true
        track('scan_demo_impression', { cta_surface: resolvedSurface, page_type: pageType, method_version: 'visible_video_2026_10_06' })
      }
      if (visible) el.play().catch(() => {})
      if (!visible && !el.paused) el.pause()
    }, { threshold: [0, 0.4] })
    observer.observe(el)
    return () => observer.disconnect()
  }, [resolvedSurface, pageType])

  const handlePlaying = () => {
    if (trackedPlay.current) return
    trackedPlay.current = true
    track('scan_demo_play', { cta_surface: resolvedSurface, page_type: pageType, method_version: 'visible_video_2026_10_06' })
  }

  const video = <figure className="relative w-fit mx-auto md:mx-0">
    <div aria-hidden="true" className="absolute -inset-4 sm:-inset-6 rounded-[40px] bg-gradient-to-br from-warmOrange/20 via-warmOrange/5 to-transparent blur-2xl pointer-events-none" />
    <IPhoneFrame island={false} className={variant === 'inline' ? 'w-[min(82vw,260px)]' : 'w-[min(88vw,300px)] sm:w-[300px] md:w-[320px]'}>
      <video ref={videoRef} width={540} height={1174}
        className="absolute inset-0 h-full w-full object-cover"
        autoPlay playsInline muted loop preload="metadata" poster="/scan-demo/scan-demo-poster.jpg"
        aria-label="Recorded bottled-water product-assessment workflow in the app"
        onPlaying={handlePlaying}>
        <source src="/scan-demo/scan-demo.webm" type="video/webm" />
        <source src="/scan-demo/scan-demo.mp4" type="video/mp4" />
        <p className="p-4 text-sm text-white">Your browser cannot play this recorded product-review example.</p>
      </video>
    </IPhoneFrame>
    <figcaption className="relative mt-5 max-w-[320px] text-xs text-gray-600 leading-relaxed text-center">
      Recorded interface example. On-screen scores, exposure labels and suggested swaps are modeled outputs, not particle measurements, personal exposure or evidence of a health benefit.
    </figcaption>
  </figure>

  if (variant === 'inline') return <div className="not-prose my-6 overflow-hidden">{video}</div>

  return <section className="relative w-full overflow-hidden bg-gradient-to-b from-cream to-white py-14 sm:py-section" aria-labelledby="scan-demo-heading">
    <div className="frame grid grid-cols-1 md:grid-cols-12 gap-10 items-center">
      <div className="md:col-span-5 flex justify-center md:justify-start">{video}</div>
      <div className="md:col-span-7">
        <p className="text-xs sm:text-sm font-semibold uppercase tracking-[0.2em] text-forest mb-3">{eyebrow ?? 'Recorded in the app'}</p>
        <h2 id="scan-demo-heading" className="font-display text-[clamp(1.75rem,4.5vw,2.5rem)] font-bold text-ink leading-[1.1] mb-4">{headline ?? 'See the product-review workflow.'}</h2>
        <p className="text-base sm:text-lg text-gray-600 mb-7 leading-relaxed">{sub ?? 'A bottled-water example shows the first scan, a modeled assessment and a suggested material alternative. It plays on a loop, right here on the page.'}</p>
        <ol className="space-y-5 mb-8">
          {MOMENTS.map((moment, i) => <li key={moment.label} className="flex gap-4">
            <span aria-hidden="true" className="shrink-0 inline-flex items-center justify-center w-8 h-8 rounded-full bg-ink text-cream text-xs font-mono">0{i + 1}</span>
            <div><h3 className="font-semibold text-ink text-base">{moment.label}</h3><p className="text-sm text-gray-600 mt-1 leading-relaxed">{moment.detail}</p></div>
          </li>)}
        </ol>
        <div className="flex flex-col sm:flex-row gap-3 sm:items-center">
          <StoreBadges
            source={`${resolvedSurface}_appstore`}
            ctaPosition="scan_demo"
            trackingParams={{ page_type: pageType }}
          />
        </div>
        <p className="mt-4 text-xs text-gray-600">Free download with in-app purchases. Review current terms in each store.</p>
      </div>
    </div>
  </section>
}
